import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { apiGet, apiSend, fmtDate } from '../api';
import { theme, card, input, btnPrimary, btnSecondary } from '../theme';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
import { tradeLabel, getTrades } from '../utils/trades';
import { useActiveTrades } from '../utils/useTrades';
// v0.17.1 — Lucide migration.
import { FileText, ClipboardList, Link2 } from 'lucide-react';
import { stampOf, listState, clientOptions, stampLine, stampBody, stampRefusal } from '../utils/clientCompany';

// ---------------------------------------------------------------------------
// ReportsScreen — v0.9.0. List of saved Report configurations per project.
//
// Route: /projects/:projectId/reports
//
// Each row is a saved /api/reports record: name, description, settingsJson.
// Per-row actions:
//   • Open       → /projects/:id/report?reportId=N (ReportScreen reads
//                  settingsJson, applies, and renders the PDF live)
//   • Share      → /projects/:id/report?reportId=N + auto-open ShareModal
//                  (handled by ReportScreen at the destination; here we
//                  just navigate with ?reportId=N and let the user click
//                  "Share link" — keeps the flow consistent.)
//   • Edit       → opens the inline editor (name/description; settings
//                  are edited on the report screen itself)
//   • Delete     → DELETE /api/reports/:id with a confirm
//
// "+ New report" button → POST /api/reports with default settings, then
// navigate into the new report so the user lands on the settings panel.
//
// v0.9.0 — initial.
// ---------------------------------------------------------------------------
export default function ReportsScreen() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Inline editor for name/description (the settings panel lives on
  // ReportScreen so we don't duplicate the controls).
  const [editing, setEditing] = useState(null);  // { id, name, description }
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', description: '' });

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true); setError('');
    try {
      const rows = await apiGet(`/api/reports?projectId=${encodeURIComponent(projectId)}`);
      setReports(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // v0.330.0 — the client companies this builder may stamp a report for
  // (GET /api/client-workspaces). Read fresh: a company linked a minute ago
  // must be pickable now. A list that cannot be read says so; it is never
  // shown as "no client companies".
  const [clients, setClients] = useState({ state: 'loading', list: [] });
  const loadClients = useCallback(async () => {
    setClients(c => ({ ...c, state: 'loading' }));
    try {
      const j = await apiGet('/api/client-workspaces', { fresh: true });
      setClients({ state: 'ok', list: Array.isArray(j && j.clients) ? j.clients : [] });
    } catch (e) {
      setClients({ state: 'failed', list: [], error: e });
    }
  }, []);
  useEffect(() => { loadClients(); }, [loadClients]);

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setDraft({ name: 'New report', description: '' });
  };
  // v0.330.0 — THE CLIENT COMPANY (replaces v0.178's "Visible to the client"
  // tick, which showed a report to a whole client company). Brett: "Jane would
  // need to be a client." and "Clients dont see trade stuff, trades dont see
  // clients." A report reaches a client person's DropPin home only when it is
  // stamped for their company here AND shared with them by name.
  //
  // Not optimistic and never queued: the row keeps its old company until the
  // server says yes, and a refusal or no connection says why on that row. A
  // stamp replayed from the outbox later would land after the list changed.
  const [stamping, setStamping] = useState({});   // { [reportId]: { busy, error } }
  const setClientCompany = async (r, value) => {
    let body;
    try { body = stampBody(value); } catch (e) { return; }
    setStamping(s => ({ ...s, [r.id]: { busy: true, error: '' } }));
    try {
      const updated = await apiSend('PATCH', `/api/reports/${r.id}`, body, { noQueue: true });
      const next = updated && updated.clientWorkspaceId !== undefined ? updated.clientWorkspaceId : body.clientWorkspaceId;
      setReports(prev => prev.map(x => (x.id === r.id ? { ...x, clientWorkspaceId: next } : x)));
      setStamping(s => ({ ...s, [r.id]: { busy: false, error: '' } }));
    } catch (e) {
      setStamping(s => ({ ...s, [r.id]: { busy: false, error: stampRefusal(e) } }));
    }
  };

  const startEdit = (r) => {
    setEditing(r);
    setCreating(false);
    setDraft({ name: r.name || '', description: r.description || '' });
  };
  const cancelDraft = () => { setEditing(null); setCreating(false); };

  const saveDraft = async () => {
    if (!draft.name.trim()) return;
    setBusy(true);
    try {
      if (creating) {
        const row = await apiSend('POST', '/api/reports', {
          projectId: Number(projectId),
          name: draft.name.trim(),
          description: draft.description.trim() || null,
        });
        setReports(prev => [row, ...prev]);
        // Jump straight to the report so the user can adjust settings.
        navigate(`/projects/${projectId}/report?reportId=${row.id}`);
      } else if (editing) {
        const updated = await apiSend('PATCH', `/api/reports/${editing.id}`, {
          name: draft.name.trim(),
          description: draft.description.trim() || null,
        });
        setReports(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
        cancelDraft();
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const deleteReport = async (r) => {
    if (!confirm(`Delete report "${r.name}"? Share links pointing at this report will stay valid but become unlinked.`)) return;
    setBusy(true);
    try {
      await apiSend('DELETE', `/api/reports/${r.id}`);
      setReports(prev => prev.filter(x => x.id !== r.id));
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button onClick={() => navigate(`/projects/${projectId}`)} style={{ ...btnSecondary, marginBottom: 12 }}>← Back to Location</button>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <PageHeading style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><FileText size={20} /> Saved reports</PageHeading>
            <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>
              {reports.length} saved {reports.length === 1 ? 'report' : 'reports'} · each can have its own filters, sections, paper size and client name.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => navigate(`/projects/${projectId}/report`)} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Open the ad-hoc live report (not saved)">
              <ClipboardList size={14} /> Quick report
            </button>
            {/* v0.9.4 - Shared links moved here from the project home grid.
                Brett's "INSIDE REPORTS" markup: the share workflow belongs
                next to the reports themselves, not as a separate top-level
                tile on the project home. */}
            <button onClick={() => navigate(`/projects/${projectId}/shares`)} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Mint and manage read-only share links for this project">
              <Link2 size={14} /> Manage shares
            </button>
            <button onClick={startCreate} style={btnPrimary}>+ New report</button>
          </div>
        </div>
      </div>

      {(creating || editing) && (
        <div style={{ ...card, marginBottom: 12, borderColor: theme.brand, background: theme.bg }}>
          <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0, marginBottom: 10 }}>
            {creating ? 'New report' : `Editing: ${editing.name}`}
          </h2>
          <div style={{ display: 'grid', gap: 8 }}>
            <Field label="Name">
              <input
                autoFocus
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                style={input}
                placeholder="e.g. Practical completion handover"
              />
            </Field>
            <Field label="Description (optional)">
              <input
                value={draft.description}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                style={input}
                placeholder="Internal note about who this is for / what filters it uses"
              />
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={saveDraft} disabled={busy || !draft.name.trim()}
                style={{ ...btnPrimary, opacity: (busy || !draft.name.trim()) ? 0.6 : 1 }}>
                {creating ? 'Create + open' : 'Save'}
              </button>
              <button onClick={cancelDraft} disabled={busy} style={btnSecondary}>Cancel</button>
            </div>
          </div>
          {creating && (
            <div style={{ marginTop: 8, fontSize: 11, color: theme.textLight }}>
              You'll be taken to the report view where you can adjust filters + sections + paper size.
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ ...card, color: theme.danger, marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: theme.textLight }}>
          Loading saved reports…
        </div>
      ) : reports.length === 0 ? (
        <EmptyState onCreate={startCreate} />
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {reports.map(r => (
            <ReportRow
              key={r.id}
              report={r}
              busy={busy}
              onOpen={() => navigate(`/projects/${projectId}/report?reportId=${r.id}`)}
              onEdit={() => startEdit(r)}
              onDelete={() => deleteReport(r)}
              clients={clients}
              stamping={stamping[r.id]}
              onRetryClients={loadClients}
              onClientCompany={(value) => setClientCompany(r, value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportRow({ report: r, busy, onOpen, onEdit, onDelete, clients, stamping, onRetryClients, onClientCompany }) {
  const summary = summariseSettings(r.settingsJson);
  return (
    <div
      style={{
        ...card,
        display: 'flex', alignItems: 'flex-start', gap: 12,
        flexWrap: 'wrap',
        cursor: 'pointer',
      }}
      onClick={(e) => {
        // Skip when clicking a button inside the row.
        if (e.target && e.target.tagName === 'BUTTON') return;
        onOpen();
      }}
    >
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: theme.text }}>{r.name || 'Untitled report'}</div>
        {r.description && (
          <div style={{ fontSize: 12, color: theme.textLight, marginTop: 2 }}>{r.description}</div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {summary.map((s, i) => (
            <span key={i} style={{
              fontSize: 11, fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 999,
              background: theme.bg,
              color: theme.textLight,
              border: `1px solid ${theme.border}`,
            }}>{s}</span>
          ))}
        </div>
        <div style={{ fontSize: 11, color: theme.textLight, marginTop: 8 }}>
          Created {fmtDate(r.createdAt)}{r.updatedAt && r.updatedAt !== r.createdAt ? ` · updated ${fmtDate(r.updatedAt)}` : ''}
        </div>

        <ClientCompany
          report={r}
          clients={clients}
          stamping={stamping}
          disabled={busy}
          onRetry={onRetryClients}
          onPick={onClientCompany}
        />
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button onClick={onOpen}   style={btnPrimary}   title="Open / generate this report">Open</button>
        <button onClick={onEdit}   disabled={busy} style={btnSecondary}>Edit name</button>
        <button onClick={onDelete} disabled={busy} style={{ ...btnSecondary, color: theme.danger, borderColor: theme.danger }}>Delete</button>
      </div>
    </div>
  );
}

// v0.330.0 — the report's client company. The select is its own width (never
// stretched), the line under it says in words what the pick means, and the
// list's state is named: reading, could not be read (Try again), none linked
// yet, or a stamp that is no longer linked. Clicks here never open the report.
function ClientCompany({ report: r, clients, stamping, disabled, onRetry, onPick }) {
  const stamp = stampOf(r);
  const st = listState(clients);
  const opts = clientOptions(clients, stamp);
  const busy = !!(stamping && stamping.busy);
  const error = stamping && stamping.error;
  const canPick = (st === 'ok' || st === 'none') && !busy && !disabled;
  const stale = opts.some(o => o.stale);
  return (
    <div
      data-client-company={r.id}
      data-state={busy ? 'saving' : st}
      onClick={(e) => e.stopPropagation()}
      style={{ marginTop: 10, display: 'grid', gap: 4, justifyItems: 'start' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <label htmlFor={`client-co-${r.id}`} style={{ fontSize: 11, fontWeight: 700, color: theme.textLight }}>
          Client company
        </label>
        <select
          id={`client-co-${r.id}`}
          value={stamp == null ? '' : String(stamp)}
          disabled={!canPick}
          onChange={(e) => onPick && onPick(e.target.value)}
          style={{ ...input, width: 'auto', maxWidth: '100%', minWidth: 160, padding: '4px 8px', fontSize: 12,
            borderColor: stale ? theme.warn : theme.border, cursor: canPick ? 'pointer' : 'default' }}
        >
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {busy && <span style={{ fontSize: 11, color: theme.textLight }}>Saving…</span>}
        {st === 'loading' && !busy && <span style={{ fontSize: 11, color: theme.textLight }}>Reading your client companies…</span>}
      </div>
      {st === 'failed' && (
        <div data-client-list-failed style={{ fontSize: 11, color: theme.danger, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          Your client companies could not be read, so this cannot be changed right now.
          <button type="button" onClick={onRetry} style={{ ...btnSecondary, padding: '2px 10px', fontSize: 11 }}>Try again</button>
        </div>
      )}
      <div data-client-line style={{ fontSize: 11, color: stale ? theme.warn : theme.textLight, maxWidth: 520 }}>
        {stampLine(clients, stamp)}
      </div>
      {error && (
        <div role="alert" data-client-error style={{ fontSize: 11, color: theme.danger, maxWidth: 520 }}>{error}</div>
      )}
    </div>
  );
}

// Turn the saved settingsJson into a couple of human-readable chips for the
// row preview. Robust to missing/bad JSON.
function summariseSettings(raw) {
  let s = null;
  try {
    if (!raw) return ['default settings'];
    s = (typeof raw === 'string') ? JSON.parse(raw) : raw;
  } catch { return ['(unreadable settings)']; }
  if (!s || typeof s !== 'object') return ['default settings'];
  const chips = [];
  chips.push((s.paperSize === 'letter') ? 'Letter' : 'A4');
  if (s.clientName) chips.push(`for ${String(s.clientName).slice(0, 20)}`);
  const f = s.filters || {};
  const arrCount = (a) => Array.isArray(a) ? a.length : 0;
  const t = arrCount(f.trade);
  if (t > 0 && t < getTrades().length) chips.push(`${t} trade${t === 1 ? '' : 's'}`);
  const st = arrCount(f.status);
  if (st > 0) chips.push(`status: ${(f.status || []).map(x => x.replace('_', ' ')).join(' + ')}`);
  const pr = arrCount(f.priority);
  if (pr > 0) chips.push(`priority: ${(f.priority || []).join(' + ')}`);
  const dr = arrCount(f.drawing);
  if (dr > 0) chips.push(`${dr} drawing${dr === 1 ? '' : 's'}`);
  const sections = [];
  if (s.includeCover === false)      sections.push('no cover');
  if (s.includePhotos === false)     sections.push('no photos');
  if (s.includePlanThumbs === false) sections.push('no plans');
  if (s.includeSignoff === true)     sections.push('sign-off');
  if (sections.length) chips.push(sections.join(', '));
  return chips.length ? chips : ['default settings'];
}

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div style={{ ...card, padding: 40, textAlign: 'center' }}>
      <FileText size={48} color={theme.textLight} strokeWidth={1.5} />
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 8 }}>No saved reports yet</div>
      <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
        Save a report config to re-run it with the same filters and settings later — useful for the same handover going out monthly, or per-trade hand-offs to a builder.
      </div>
      <button onClick={onCreate} style={{ ...btnPrimary, marginTop: 14 }}>+ Create your first saved report</button>
    </div>
  );
}
